<template>
	<v-container
		fluid
		:class = "['access-control-device-page', embedded ? 'access-control-device-page--embedded pa-0' : 'pa-4']"
	>
		<v-row no-gutters>
			<v-col cols = "12">
				<v-card outlined class = "access-control-device-card access-control-settings-region access-control-settings-region--device">
					<v-card-title class = "justify-center body-1 secondary pa-1 white--text" v-if = "!embedded">
						<v-icon small color = "white" class = "ml-1" @click = "$router.back()">mdi-arrow-left-circle</v-icon>
						<v-spacer></v-spacer>
						<v-icon color = "primary" class = "mr-2">mdi-door-closed-lock</v-icon>
						<span>ACCESS CONTROL SETTINGS</span>
						<v-spacer></v-spacer>
					</v-card-title>

					<v-card-title class = "pa-0 caption secondary white--text vue-draggable-handle" v-else>
						<v-icon x-small color = "primary" class = "px-2">mdi-video</v-icon>
						<span>{{ $ml.get('device') }}</span>
					</v-card-title>

					<v-card-text class = "pb-1" v-if = "!embedded">
						<v-form ref = "form" v-model = "valid" class = "access-control-settings-form">
							<v-row class = "mt-2 access-control-settings-region access-control-settings-region--identity">
								<v-col xl = "4" lg = "4" md = "6" sm = "12" cols = "12" class = "access-control-settings-field access-control-settings-field--connection">
									<v-text-field dense outlined v-model = "form.host" :label = "$ml.get('ip') || 'Host/IP'" :rules = "requiredRules"></v-text-field>
								</v-col>
								<v-col xl = "4" lg = "4" md = "6" sm = "12" cols = "12" class = "access-control-settings-field access-control-settings-field--connection">
									<v-text-field dense outlined v-model = "form.username" :label = "$ml.get('username') || 'Username'" :rules = "requiredRules"></v-text-field>
								</v-col>
								<v-col xl = "4" lg = "4" md = "6" sm = "12" cols = "12" class = "access-control-settings-field access-control-settings-field--connection">
								<v-text-field
									dense
									outlined
									:type = "passwordFieldType"
									v-model = "form.password"
									:label = "$ml.get('password') || 'Password'"
									:placeholder = "passwordPlaceholder"
									:hint = "passwordHint"
									:persistent-hint = "isEdit"
									:rules = "passwordRules"
									:append-icon = "passwordAppendIcon"
									:loading = "passwordRevealLoading"
									@click:append = "togglePasswordVisibility"
								></v-text-field>
								</v-col>
								<v-col xl = "4" lg = "4" md = "6" sm = "12" cols = "12" class = "access-control-settings-field access-control-settings-field--identity">
									<v-text-field dense outlined v-model = "form.name" :label = "$ml.get('name')"></v-text-field>
								</v-col>
								<v-col cols = "12" class = "access-control-settings-region access-control-settings-region--media access-control-settings-field--rtsp">
									<v-text-field
										dense
										outlined
										v-model = "form.rtsp"
										label = "RTSP stream URL"
										placeholder = "rtsp://host:554/cam/realmonitor"
										:rules = "rtspRules"
										:append-icon = "showPreview ? 'mdi-eye-off' : 'mdi-cctv'"
										@click:append = "testStream"
									></v-text-field>
								</v-col>
								<v-col cols = "12" v-if = "shouldRenderInternalPreview" class = "access-control-preview-column access-control-settings-region access-control-settings-region--media-preview">
									<v-alert v-if = "previewError" dense outlined type = "warning" class = "mb-2">
										{{ previewError }}
									</v-alert>
									<v-card outlined class = "access-control-preview-card">
										<v-card-title class = "caption py-1">
											<v-icon small color = "primary" class = "mr-1">mdi-cctv</v-icon>
											RTSP preview
										</v-card-title>
										<v-card-text class = "pa-0 access-control-preview-frame">
											<cam_test
												:id_stream = "previewStreamId"
												:layout_id = "previewStreamId"
												:hostname = "previewHostname"
												:info_stream = "previewInfo"
												:isNotShowRedirect = "true"
												:isNotShowListAlarms = "true"
												@preview_failed = "handlePreviewFailure"
												@preview-error = "handlePreviewFailure"
												@error.native = "handlePreviewFailure"
											></cam_test>
										</v-card-text>
									</v-card>
								</v-col>
								<v-col cols = "12" class = "access-control-location-selector access-control-settings-region access-control-settings-region--location">
									<EditableMapComponent
										:title = "$ml.get('location') || 'Location'"
										:info_to_component = "mapLocation"
										@receiveInfo = "setLocation"
									></EditableMapComponent>
									<div v-if = "showLocationRequired" class = "error--text caption mt-1">
										{{ $ml.get('location') || 'Location' }} required
									</div>
								</v-col>
								<v-col xl = "4" lg = "4" md = "6" sm = "12" cols = "12" v-if = "isEdit && form.deviceId" class = "access-control-settings-field access-control-settings-field--identity">
									<v-text-field dense outlined readonly v-model = "form.deviceId" label = "Device ID"></v-text-field>
								</v-col>
								<v-col xl = "4" lg = "4" md = "6" sm = "12" cols = "12" v-if = "isEdit && form.model" class = "access-control-settings-field access-control-settings-field--identity">
									<v-text-field dense outlined readonly v-model = "form.model" label = "Model"></v-text-field>
								</v-col>
								<v-col xl = "4" lg = "4" md = "6" sm = "12" cols = "12" v-if = "isEdit && form.serial" class = "access-control-settings-field access-control-settings-field--identity">
									<v-text-field dense outlined readonly v-model = "form.serial" label = "Serial"></v-text-field>
								</v-col>
								<v-col cols = "12" v-if = "isEdit">
									<v-alert dense outlined type = "info">
										Saved credentials are redacted. Enter a new password only when rotating it.
									</v-alert>
								</v-col>
							</v-row>
						</v-form>
					</v-card-text>

					<v-card-text class = "pa-1 pb-0 access-control-device-card__embedded-body" v-else>
						<v-form ref = "form" v-model = "valid" class = "access-control-settings-form">
							<v-tabs
								dark
								fixed-tabs
								v-model = "tab"
								background-color = "primary"
								height = "20"
							>
								<v-tab>INFO</v-tab>
								<v-tab>MAP</v-tab>
							</v-tabs>

							<v-tabs-items v-model = "tab">
								<v-tab-item>
									<v-row dense class = "mt-1 access-control-settings-region access-control-settings-region--embedded-info access-control-settings-region--identity">
										<v-col cols = "12" class = "access-control-settings-region--channel access-control-settings-region--media access-control-settings-field--rtsp">
											<label class = "caption grey--text">Channel 1</label>
											<v-text-field dense outlined hide-details v-model = "form.rtsp" prepend-inner-icon = "mdi-video" label = "RTSP"></v-text-field>
											<div class = "caption access-control-stream-links">
												<div><strong class = "green--text">LOCAL</strong> {{ localRtsp }}</div>
												<div><strong class = "orange--text">HLS</strong> {{ hlsUrl }}</div>
											</div>
										</v-col>

										<v-col cols = "6" class = "access-control-settings-field--identity">
											<v-text-field dense outlined v-model = "form.name" :label = "$ml.get('name')" prepend-inner-icon = "mdi-map-marker" counter = "30"></v-text-field>
										</v-col>
										<v-col cols = "6" class = "access-control-settings-field--identity">
											<v-text-field dense outlined readonly v-model = "form.deviceId" label = "UUID" prepend-inner-icon = "mdi-video"></v-text-field>
										</v-col>

										<v-col cols = "6" class = "access-control-settings-field--connection">
											<v-text-field dense outlined v-model = "form.host" :label = "$ml.get('ip') || 'Host/IP'" prepend-inner-icon = "mdi-ip" :rules = "requiredRules"></v-text-field>
										</v-col>
										<v-col cols = "6" class = "access-control-settings-field--connection">
											<v-text-field dense outlined v-model = "form.username" :label = "$ml.get('username') || 'Username'" prepend-inner-icon = "mdi-account" :rules = "requiredRules"></v-text-field>
										</v-col>
										<v-col cols = "6" class = "access-control-settings-field--connection">
											<v-text-field dense outlined :type = "passwordFieldType" v-model = "form.password" :label = "$ml.get('password') || 'Password'" :placeholder = "passwordPlaceholder" prepend-inner-icon = "mdi-lock" :hint = "passwordHint" :persistent-hint = "isEdit" :rules = "passwordRules" :append-icon = "passwordAppendIcon" :loading = "passwordRevealLoading" @click:append = "togglePasswordVisibility"></v-text-field>
										</v-col>
										<v-col cols = "6" v-if = "isEdit && form.serial">
											<v-text-field dense outlined readonly v-model = "form.serial" label = "Serial" prepend-inner-icon = "mdi-barcode"></v-text-field>
										</v-col>
										<v-col cols = "6" v-if = "isEdit && form.model">
											<v-text-field dense outlined readonly v-model = "form.model" label = "Model" prepend-inner-icon = "mdi-devices"></v-text-field>
										</v-col>
									</v-row>
								</v-tab-item>

								<v-tab-item>
									<div class = "access-control-location-selector access-control-settings-region access-control-settings-region--location access-control-location-selector--embedded">
										<EditableMapComponent
											:title = "$ml.get('location') || 'Location'"
											:info_to_component = "mapLocation"
											@receiveInfo = "setLocation"
										></EditableMapComponent>
									</div>
								</v-tab-item>
							</v-tabs-items>
						</v-form>
					</v-card-text>
					<v-divider></v-divider>
					<v-card-actions>
						<v-spacer></v-spacer>
						<v-btn x-small color = "primary" :loading = "loading" @click = "save">
							{{ isEdit ? $ml.get('update') : $ml.get('register') }}
						</v-btn>
						<v-spacer></v-spacer>
					</v-card-actions>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script>
	import EventBus from '@/event-bus'
	import EditableMapComponent from '@/components/EditableMapComponent.vue'
	import cam_test from '@/components/cam_test.vue'
	import AccessControlService, { sanitizeAccessControlDevice } from '@/services/accessControl.service.js'

	const emptyForm = () => ({
		id: null,
		deviceId: '',
		name: '',
		brand: '',
		model: '',
		host: '',
		username: '',
		password: '',
		serial: '',
		longitude: '',
		latitude: '',
		rtsp: '',
		type: 'ACCESS_CONTROL',
	})

	export default {
		name: 'access_control_device',
		components: {
			EditableMapComponent,
			cam_test,
		},
		props: [
			'info_to_device',
			'info_device',
			'embedded',
			'external_media_preview',
		],
		data: () => ({
			tab: null,
			valid: false,
			loading: false,
			form: emptyForm(),
			hasPassword: false,
			locationTouched: false,
			saveAttempted: false,
			showPreview: false,
			previewError: '',
			showPassword: false,
			passwordRevealLoading: false,
			requiredRules: [value => !!value || 'Required'],
			rtspRules: [value => !value || /^rtsp:\/\//i.test(value) || 'RTSP URL must start with rtsp://'],
		}),
		computed: {
			localRtsp(){
				return this.form.rtsp || '-'
			},
			hlsUrl(){
				return this.buildHlsUrl(this.form.deviceId, 1)
			},
			isEdit(){
				return !!(this.form.id || this.info_to_device || this.info_device)
			},
			passwordRules(){
				return this.isEdit ? [] : this.requiredRules
			},
			passwordHint(){
				return this.isEdit && this.hasPassword && !this.form.password ? 'Saved credential available. Use the eye icon to reveal it.' : ''
			},
			passwordPlaceholder(){
				return this.isEdit && this.hasPassword && !this.form.password ? '••••••••' : ''
			},
			passwordFieldType(){
				return this.showPassword ? 'text' : 'password'
			},
			passwordAppendIcon(){
				return this.showPassword ? 'mdi-eye-off' : 'mdi-eye'
			},
			mapLocation(){
				return this.hasLocation ? {
					longitude: this.form.longitude,
					latitude: this.form.latitude,
				} : null
			},
			hasLocation(){
				return this.hasCoordinate(this.form.longitude) && this.hasCoordinate(this.form.latitude)
			},
			showLocationRequired(){
				return this.saveAttempted && !this.hasLocation
			},
			isRtspValid(){
				return !this.form.rtsp || /^rtsp:\/\//i.test(this.form.rtsp)
			},
			previewStreamId(){
				return this.form.deviceId || 'access-control-preview'
			},
			previewHostname(){
				return this.form.name || this.form.host || 'ACCESS CONTROL'
			},
			previewInfo(){
				return {
					...this.form,
					rtsp: this.form.rtsp,
					type: 'ACCESS_CONTROL',
				}
			},
			backendRtspDependencyMessage(){
				return 'RTSP persistence requires backend support. Save without RTSP or update the backend dependency.'
			},
			previewFailureMessage(){
				return 'RTSP preview is unavailable through the current media path. You can keep editing the form.'
			},
			shouldRenderInternalPreview(){
				return this.showPreview && !this.external_media_preview
			},
		},
		watch: {
			info_to_device: {
				handler(val){
					if (val) this.renderInfo(val)
				},
				immediate: true,
			},
			info_device: {
				handler(val){
					if (val) this.renderInfo(val)
				},
				immediate: true,
			},
		},
		created(){
			if (!this.info_to_device && !this.info_device && this.shouldHydrateFromLocalSettings()) {
				this.loadFromLocalSettings()
			}
		},
		methods: {
			shouldHydrateFromLocalSettings(){
				return !(this.$route && this.$route.name === 'new_access_control')
			},
			loadFromLocalSettings(){
				try {
					const raw = localStorage.getItem('settings_device')
					if (!raw) return

					const parsed = JSON.parse(raw)
					const item = parsed && parsed.inf_setting ? parsed.inf_setting : null
					if (item && item.type === 'ACCESS_CONTROL') {
						this.renderInfo(item)
						this.refreshDeviceDetail(item)
					}
				} catch (e) {}
			},
			async refreshDeviceDetail(item){
				const deviceId = item && (item.deviceId || item.device_id || item.id)
				if (!deviceId) return

				try {
					const res = await AccessControlService.getDevice(deviceId)
					if (!res || !res.data) return

					const detail = { ...item, ...res.data, type: 'ACCESS_CONTROL' }
					this.renderInfo(detail)
					localStorage.setItem('settings_device', JSON.stringify({ inf_setting: sanitizeAccessControlDevice(detail) }))
				} catch (e) {}
			},
			renderInfo(item){
				const rawRtsp = item && (item.rtsp || item.rtspUrl || item.streamUrl) ? (item.rtsp || item.rtspUrl || item.streamUrl) : ''
				const sanitized = sanitizeAccessControlDevice(item) || {}
				this.form = {
					...emptyForm(),
					...sanitized,
					deviceId: sanitized.deviceId || sanitized.device_id || sanitized.id || '',
					host: sanitized.host || sanitized.ip || '',
					brand: sanitized.brand || '',
					model: sanitized.model || '',
					serial: sanitized.serial || sanitized.deviceSn || sanitized.device_sn || '',
					longitude: sanitized.longitude !== undefined && sanitized.longitude !== null ? sanitized.longitude : '',
					latitude: sanitized.latitude !== undefined && sanitized.latitude !== null ? sanitized.latitude : '',
					username: sanitized.username || sanitized.user || '',
					password: '',
					rtsp: rawRtsp,
					type: 'ACCESS_CONTROL',
				}
				this.hasPassword = !!(sanitized.hasPassword || sanitized.has_password || sanitized.credentialRedacted)
				this.showPreview = false
				this.previewError = ''
				this.locationTouched = false
				this.saveAttempted = false
			},
			hasCoordinate(value){
				return value !== undefined && value !== null && value !== '' && !Number.isNaN(Number(value))
			},
			buildHlsUrl(deviceId, channel){
				if (!deviceId) return '-'
				return `/hls/${deviceId}/${channel}/index.m3u8`
			},
			setLocation(location = {}){
				this.form.longitude = location.longitude
				this.form.latitude = location.latitude
				this.locationTouched = true
			},
			notify(type, message){
				const bus = this.$EventBus || EventBus
				bus.$emit('notifications_modal', { type, message })
			},
			testStream(){
				if (!this.form.rtsp) {
					this.showPreview = false
					this.previewError = ''
					this.notify('warning', 'RTSP stream URL required')
					return
				}
				if (!this.isRtspValid) {
					this.showPreview = false
					this.previewError = ''
					this.notify('warning', 'RTSP URL must start with rtsp://')
					return
				}
				this.previewError = ''
				this.showPreview = !this.showPreview
			},
				handlePreviewFailure(){
				this.previewError = this.previewFailureMessage
				this.showPreview = true
				this.notify('warning', this.previewFailureMessage)
			},
			async togglePasswordVisibility(){
				if (this.showPassword) {
					this.showPassword = false
					return
				}

				if (this.isEdit && this.hasPassword && !this.form.password) {
					const revealed = await this.revealSavedPassword()
					if (!revealed) return
				}
				this.showPassword = true
			},
			async revealSavedPassword(){
				const deviceId = this.form.deviceId || this.form.id
				if (!deviceId) return false

				this.passwordRevealLoading = true
				try {
					const res = await AccessControlService.getDevicePassword(deviceId)
					const password = res && res.data ? res.data.password : ''
					if (!password) {
						this.notify('warning', 'Saved password is unavailable')
						return false
					}
					this.form.password = password
					this.hasPassword = true
					return true
				} catch (e) {
					this.notify('warning', 'Saved password is unavailable')
					return false
				} finally {
					this.passwordRevealLoading = false
				}
			},
			isRtspPersistenceError(error){
				if (!this.form.rtsp) return false
				const status = error && error.response ? error.response.status : null
				if (status === 501) return true

				const data = error && error.response ? error.response.data : null
				const message = typeof data === 'string' ? data : JSON.stringify(data || '')
				return /rtsp|stream|unsupported|unknown field|persist|dependency/i.test(message)
			},
			buildPayload(){
				const payload = {
					host: this.form.host,
					username: this.form.username,
					password: this.form.password,
					name: this.form.name,
					longitude: this.form.longitude,
					latitude: this.form.latitude,
					rtsp: this.form.rtsp ? this.form.rtsp.trim() : '',
				}
				if (this.isEdit && !payload.password) delete payload.password
				if (!this.isEdit && !payload.rtsp) delete payload.rtsp
				return payload
			},
			async save(){
				this.saveAttempted = true
				if (this.$refs.form && !this.$refs.form.validate()) return
				if (!this.isRtspValid) {
					this.notify('warning', 'RTSP URL must start with rtsp://')
					return
				}
				if (!this.hasLocation) {
					this.notify('warning', `${this.$ml.get('location') || 'Location'} required`)
					return
				}
				const permission = this.isEdit ? 'can_edit' : 'can_add'
				if (!this.$store.getters['auth/permission'](this.$route.name || 'new_access_control', permission, 'device')) {
					this.notify('warning', this.$ml.get('no_have_permission_create') || 'Permission denied')
					return
				}

				this.loading = true
				try {
					const payload = this.buildPayload()
					const key = this.form.deviceId || this.form.id
					const hadPassword = this.hasPassword
					const passwordWasProvided = !!payload.password
					const res = this.isEdit ? await AccessControlService.updateDevice(key, payload) : await AccessControlService.createDevice(payload)
					const responseData = res && res.data ? res.data : {}
					const saved = sanitizeAccessControlDevice({
						...this.form,
						...payload,
						...responseData,
						deviceId: responseData.deviceId || responseData.device_id || responseData.id || this.form.deviceId || this.form.id,
						host: responseData.host || responseData.ip || payload.host || this.form.host,
						username: responseData.username || responseData.user || payload.username || this.form.username,
						type: 'ACCESS_CONTROL',
					})
					this.form.password = ''
					this.hasPassword = !!(saved && (saved.hasPassword || saved.has_password || saved.credentialRedacted || passwordWasProvided || hadPassword))
					if (this.hasPassword) saved.hasPassword = true

					if (this.isEdit) {
						this.$store.commit('Devices/update', saved)
						EventBus.$emit('update_device_to_comp', saved)
					} else {
						this.$store.commit('Devices/concat_devices', saved)
					}

					localStorage.setItem('settings_device', JSON.stringify({ inf_setting: saved }))
					this.notify(this.isEdit ? 'updated' : 'saved')

					if (!this.isEdit) {
						EventBus.$emit('redirect_view', 'setting_device')
						this.$router.push({ name: 'setting_device' }).catch(err => {
							if (err.name !== 'NavigationDuplicated') throw err
						})
					}
				} catch (e) {
					if (this.isRtspPersistenceError(e)) {
						this.notify('warning', this.backendRtspDependencyMessage)
					} else {
						this.notify('error')
					}
				} finally {
					this.loading = false
				}
			},
		},
	}
</script>

<style scoped>
	.access-control-location-selector{
		min-height: 420px;
	}

	.access-control-device-page{
		padding-top: 24px !important;
		max-width: 1280px;
		margin-left: auto;
		margin-right: auto;
	}

	.access-control-device-page--embedded{
		padding-top: 0 !important;
		max-width: none;
		width: 100%;
	}

	.access-control-device-card{
		margin-top: 8px;
		width: 100%;
	}

	.access-control-settings-region{
		min-width: 0;
	}

	.access-control-settings-form{
		width: 100%;
	}

	.access-control-preview-column{
		min-height: 260px;
	}

	.access-control-preview-card{
		overflow: hidden;
	}

	.access-control-preview-frame{
		aspect-ratio: 16 / 9;
		min-height: 220px;
	}
</style>
